import React, { useState, useCallback, useEffect, useRef } from 'react';
import GoogleMapLoader from './map/GoogleMapLoader';
import MapDisplay from './map/MapDisplay';
import LoadingStates from './map/LoadingStates';
import { useCampSites } from './map/useCampSites';
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarRange, Search, X } from 'lucide-react';
import { useSiteBookings } from '@/hooks/useSiteBookings';
import { useSiteBookingStatus } from '@/hooks/useSiteBookingStatus';

interface CampSiteMapProps {
  siteType: 'rv' | 'wild-horse';
  startDate?: Date;
  endDate?: Date;
  locationFilter?: string;
}

const CampSiteMap: React.FC<CampSiteMapProps> = ({
  siteType,
  startDate: initialStartDate,
  endDate: initialEndDate,
  locationFilter = 'all'
}) => {
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapKey, setMapKey] = useState(`${siteType}-${locationFilter}`);
  const [searchStartDate, setSearchStartDate] = useState<Date | undefined>(initialStartDate);
  const [searchEndDate, setSearchEndDate] = useState<Date | undefined>(initialEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | undefined>(initialStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | undefined>(initialEndDate);
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  const { sites, isLoading, error } = useCampSites({ 
    siteType, 
    locationFilter,
    startDate: appliedStartDate,
    endDate: appliedEndDate
  });

  // For each site, fetch bookings (N+1 query for now, simplest path for correct demo).
  // In production, batch by location/type/date range

  // Example for grid/map child use: Map site.id to booking info
  // --- REMOVE siteBookingMap logic since it's an unused prop that causes the error
  // const siteBookingMap = Object.fromEntries(
  //   (sites || []).map(site => {
  //     const { bookings } = useSiteBookings(site.id);
  //     return [site.id, bookings];
  //   })
  // );

  // Reset the map when siteType or locationFilter changes
  useEffect(() => {
    // Reset selected site when changing filters
    setSelectedSiteId(null);
    
    // Force map component to remount when type or location changes
    setMapKey(`${siteType}-${locationFilter}-${Date.now()}`);
    
    // Clear any stored map positions from session storage
    sessionStorage.removeItem('mapCenter');
    sessionStorage.removeItem('mapZoom');
  }, [siteType, locationFilter]);

  const handleMapsLoaded = useCallback(() => {
    setMapsLoaded(true);
  }, []);

  const handleSiteSelect = useCallback((siteId: string) => {
    setSelectedSiteId(siteId === '' ? null : siteId);
  }, []);
  
  const handleApplyDateFilter = useCallback(() => {
    setAppliedStartDate(searchStartDate);
    setAppliedEndDate(searchEndDate);
    // Force map to refresh with new dates
    setMapKey(`${siteType}-${locationFilter}-date-${Date.now()}`);
  }, [searchStartDate, searchEndDate, siteType, locationFilter]);
  
  const handleClearDateFilter = useCallback(() => {
    setSearchStartDate(undefined);
    setSearchEndDate(undefined);
    setAppliedStartDate(undefined);
    setAppliedEndDate(undefined);
    // Force map to refresh without date filter
    setMapKey(`${siteType}-${locationFilter}-${Date.now()}`);
  }, [siteType, locationFilter]);
  
  // Loading and error states
  if (isLoading || error) {
    return <LoadingStates isLoading={isLoading} error={error} />;
  }

  return (
    <div className="w-full h-full relative space-y-4">
      {/* Date Filter Section */}
      <Card className="p-3 bg-white">
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <Button 
            variant={showDateFilter ? "default" : "outline"} 
            className="w-full sm:w-auto flex items-center gap-2"
            onClick={() => setShowDateFilter(!showDateFilter)}
          >
            <CalendarRange className="h-4 w-4" />
            {showDateFilter ? "Hide Date Filter" : "Filter by Date"}
          </Button>
          
          {appliedStartDate && appliedEndDate && (
            <div className="flex items-center gap-2 text-xs md:text-sm px-3 py-1 bg-tribal-primary/10 rounded-full">
              <span className="font-medium">
                {appliedStartDate.toLocaleDateString()} - {appliedEndDate.toLocaleDateString()}
              </span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5 rounded-full"
                onClick={handleClearDateFilter}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {appliedStartDate && appliedEndDate && (
            <div className="ml-auto text-sm text-tribal-neutral">
              Showing <span className="font-medium text-tribal-primary">{sites.length}</span> available sites
            </div>
          )}
        </div>
        
        {showDateFilter && (
          <div className="mt-3 p-3 border rounded-md">
            <DateRangePicker
              startDate={searchStartDate}
              endDate={searchEndDate}
              setStartDate={setSearchStartDate}
              setEndDate={setSearchEndDate}
              startLabel="Check-in Date"
              endLabel="Check-out Date"
              className="w-full"
              displayUnit="nights"
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={handleClearDateFilter}
              >
                Clear
              </Button>
              <Button
                onClick={handleApplyDateFilter}
                disabled={!searchStartDate || !searchEndDate}
                className="flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search Availability
              </Button>
            </div>
          </div>
        )}
      </Card>
      
      {/* Map API Loader Component */}
      <GoogleMapLoader onMapsLoaded={handleMapsLoaded} />
      
      {/* Key property forces component remount when siteType, location, or dates change */}
      <MapDisplay
        key={mapKey}
        sites={sites}
        mapsLoaded={mapsLoaded}
        locationFilter={locationFilter}
        onSiteSelect={handleSiteSelect}
        selectedSiteId={selectedSiteId}
        startDate={appliedStartDate}
        endDate={appliedEndDate}
        usePopupCard={true}
        siteType={siteType}
      />
    </div>
  );
};

export default CampSiteMap;
